/**
 * FrameworkImportView -- custom-framework taxonomy CSV import (P260).
 *
 * Route: /admin/framework-import
 *
 * A bank brings its own framework + control catalogue: download the
 * template, upload a one-row-per-control CSV, review the per-row dry-run,
 * then commit. Nothing touches live tables until Commit. Imported controls
 * flow straight into the catalogue browse, assessment cycles, and the
 * obligation extractor.
 *
 * Admin-only; hardcoded English to match the sibling shared/admin views.
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import {
  downloadFrameworkTemplate,
  useCommitFrameworkBatch,
  useUploadFrameworkCsv,
} from "shared/api/hooks/useFrameworkImport";
import type { FrameworkImportBatch } from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";

function BatchReview({
  batch,
  onCommit,
  committing,
}: {
  batch: FrameworkImportBatch;
  onCommit: () => void;
  committing: boolean;
}) {
  const { t } = useTranslation();
  const committed = batch.status === "committed";
  return (
    <section
      data-testid="fw-import-batch-review"
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-700">
          <span className="font-semibold">{batch.row_count}</span>{" "}
          {t("screens.frameworkImportView.rows")}{" "}
          <span className="text-emerald-700">
            {batch.ok_count} {t("screens.frameworkImportView.ok")}
          </span>{" "}
          ·{" "}
          <span className="text-rose-700">
            {batch.error_count} {t("screens.frameworkImportView.errors")}
          </span>{" "}
          · {batch.insert_count} {t("screens.frameworkImportView.insert")}{" "}
          {batch.update_count} {t("screens.frameworkImportView.update")}
        </div>
        <button
          type="button"
          data-testid="fw-import-commit"
          onClick={onCommit}
          disabled={committed || committing || batch.ok_count === 0}
          className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {committed
            ? t("screens.frameworkImportView.committed")
            : committing
              ? t("screens.frameworkImportView.committing")
              : `Commit ${batch.ok_count} OK rows`}
        </button>
      </header>
      <div className="max-h-[28rem] overflow-y-auto">
        <table className="w-full text-start text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="text-[11px] uppercase tracking-wide text-slate-400">
              <th className="pb-1 pe-2 font-medium">#</th>
              <th className="pb-1 pe-2 font-medium">
                {t("screens.frameworkImportView.verdict")}
              </th>
              <th className="pb-1 pe-2 font-medium">
                {t("screens.frameworkImportView.action")}
              </th>
              <th className="pb-1 pe-2 font-medium">
                {t("screens.frameworkImportView.control")}
              </th>
              <th className="pb-1 font-medium">
                {t("screens.frameworkImportView.detail")}
              </th>
            </tr>
          </thead>
          <tbody>
            {batch.rows.map((r) => (
              <tr
                key={r.row_number}
                data-testid={`fw-import-row-${r.row_number}`}
                className="border-t border-slate-100 align-top"
              >
                <td className="py-1 pe-2 tabular-nums text-slate-500">
                  {r.row_number}
                </td>
                <td className="py-1 pe-2">
                  <span
                    data-verdict={r.verdict}
                    className={
                      "rounded px-1.5 py-0.5 text-[11px] font-medium " +
                      (r.verdict === "ok"
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : "bg-rose-50 text-rose-700 ring-1 ring-rose-200")
                    }
                  >
                    {r.verdict}
                  </span>
                </td>
                <td className="py-1 pe-2 text-slate-600">{r.action ?? "—"}</td>
                <td className="py-1 pe-2 font-mono text-slate-700">
                  {r.target_code ?? "—"}
                </td>
                <td className="py-1 text-slate-600">
                  {r.errors.length > 0 ? (
                    <ul className="text-rose-700">
                      {r.errors.map((e, i) => (
                        <li key={i}>
                          <span className="font-mono">{e.field}</span>: {e.message}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function FrameworkImportView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const upload = useUploadFrameworkCsv();
  const commit = useCommitFrameworkBatch();
  const toast = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [batch, setBatch] = useState<FrameworkImportBatch | null>(null);

  if (!isAdmin) {
    return <AdminOnlyPanel surface="Framework import" />;
  }

  const onUpload = () => {
    if (!file) return;
    upload.mutate(file, {
      onSuccess: (b) => {
        setBatch(b);
        toast.push({
          tone: b.error_count > 0 ? "warning" : "success",
          // Round 18 F10 -- from the catalogue.
          message: t("screens.frameworkImportView.stagedToast", {
            ok: b.ok_count,
            count: b.row_count,
          }),
          detail:
            b.error_count > 0
              ? t("screens.frameworkImportView.stagedErrors", { count: b.error_count })
              : t("screens.frameworkImportView.reviewThenCommit"),
        });
      },
      onError: (e) => toast.push({ tone: "error", message: e.message }),
    });
  };

  const onCommit = () => {
    if (!batch) return;
    commit.mutate(batch.id, {
      onSuccess: (b) => {
        setBatch(b);
        toast.push({
          tone: "success",
          message: `Committed ${b.ok_count} controls`,
          detail: `${b.insert_count} new · ${b.update_count} updated`,
        });
      },
      onError: (e) => toast.push({ tone: "error", message: e.message }),
    });
  };

  return (
    <article data-testid="framework-import-view" className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          <MarsadIcon
            name="framework"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.frameworkImportView.importACustomFramework")}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          {t("screens.frameworkImportView.bringYourOwnFrameworkControlCatalogueBeyond")}
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="fw-import-template"
            onClick={() => {
              void downloadFrameworkTemplate().catch((e) =>
                toast.push({ tone: "error", message: String(e?.message ?? e) }),
              );
            }}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10"
          >
            {t("screens.frameworkImportView.downloadCsvTemplate")}
          </button>
          <input
            type="file"
            accept=".csv,text/csv"
            data-testid="fw-import-file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-slate-600"
          />
          <button
            type="button"
            data-testid="fw-import-upload"
            onClick={onUpload}
            disabled={!file || upload.isPending}
            className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {upload.isPending
              ? t("screens.frameworkImportView.uploading")
              : t("screens.frameworkImportView.uploadDryRun")}
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          {t("screens.frameworkImportView.columnsFrameworkCodeFrameworkNameDomainId")}
        </p>
      </section>

      {batch && (
        <BatchReview batch={batch} onCommit={onCommit} committing={commit.isPending} />
      )}
    </article>
  );
}
